<script lang="ts">
import type { HTMLAttributes } from 'vue'

export interface AvatarInitialsProps {
  firstName?: string | null
  lastName?: string | null
  class?: HTMLAttributes['class']
}

function luminance(hex: string) {
  const channel = (value: number) => {
    const c = value / 255
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
  }
  const rgb = Number.parseInt(hex.slice(1), 16)
  return 0.2126 * channel(rgb >> 16) + 0.7152 * channel((rgb >> 8) & 255) + 0.0722 * channel(rgb & 255)
}

// WCAG AA for normal text; white initials only on backgrounds dark enough to reach it
function readableOnWhite(hex: string) {
  return 1.05 / (luminance(hex) + 0.05) >= 4.5
}

const palette = [
  '#BB8C23',
  '#254699',
  '#A1C490',
  '#3F6E3B',
  '#C1A9C6',
  '#4E2E77',
  '#D6A97F',
  '#C55E00',
  '#D99393',
  '#972323',
  '#DB9700',
  '#2C5CD7',
  '#89C563',
  '#0B8300',
  '#9C6FD6',
  '#510FA7',
  '#F8A75B',
  '#EE5600',
  '#FFAAAA',
  '#CC0000',
].map((backgroundColor) => ({
  backgroundColor,
  textClass: readableOnWhite(backgroundColor) ? 'text-white' : 'text-primal',
}))

function pickColor(firstName: string, lastName: string) {
  const charSum = Array.from(`${firstName} ${lastName}`).reduce((sum, char) => sum + char.charCodeAt(0), 0)
  return palette[charSum % palette.length]
}
</script>

<script setup lang="ts">
import { UserCircleIcon } from '@heroicons/vue/24/outline'
import { computed, inject } from 'vue'
import { cn } from '#ui-kit/lib/utils'
import { AVATAR_KEY } from './Avatar.vue'
import AvatarFallback from './AvatarFallback.vue'

const props = defineProps<AvatarInitialsProps>()

const avatar = inject(AVATAR_KEY, null)

// Two letters do not fit the 16px and 20px circles
const singleLetter = computed(() => avatar?.size.value === '2xs' || avatar?.size.value === 'xs')

const initials = computed(() => {
  const first = Array.from(props.firstName ?? '')[0] ?? ''
  const last = Array.from(props.lastName ?? '')[0] ?? ''
  return singleLetter.value ? first || last : `${first}${last}`
})

const color = computed(() =>
  props.firstName && props.lastName ? pickColor(props.firstName, props.lastName) : undefined,
)
</script>

<template>
  <AvatarFallback
    data-slot="avatar-initials"
    :class="cn('font-mono uppercase', color?.textClass, props.class)"
    :style="{ backgroundColor: color?.backgroundColor ?? 'transparent' }"
  >
    <span v-if="initials">{{ initials }}</span>
    <UserCircleIcon v-else class="size-full text-accent" />
  </AvatarFallback>
</template>
